import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
// import { of, switchMap } from 'rxjs'; // DESHABILITADO: usados solo por el procesado de adjuntos (ver save())

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';

import { TicketPriority, TicketType } from 'api';

import { AuthService } from '@auth/services/auth.service';
import { ToastService } from '@core/services/toast.service';
import { SupportTagsService } from '../../services/support-tags.service';
import { SupportTicketsService } from '../../services/support-tickets.service';
import { registerOptimizedImageHandler } from '../../utils/quill-image-handler';
import {
  DEFAULT_MESSAGE_LIMITS,
  analyzeMessageContent,
  formatBytes,
  getMessageLimitErrors,
} from '../../utils/message-limits';

@Component({
  selector: 'support-ticket-form',
  imports: [
    ButtonModule,
    DialogModule,
    EditorModule,
    FormsModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    TooltipModule,
  ],
  templateUrl: './ticket-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketFormComponent {
  private readonly ticketsService = inject(SupportTicketsService);
  private readonly tagsService = inject(SupportTagsService);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);

  readonly visible = input.required<boolean>();
  readonly visibleChange = output<boolean>();
  readonly saved = output<void>();

  protected readonly tags = this.tagsService.tags;

  protected title = signal('');
  protected description = signal('');
  protected type = signal<TicketType>(TicketType.Support);
  protected priority = signal<TicketPriority>(TicketPriority.Medium);
  protected selectedTagIds = signal<string[]>([]);
  protected isSaving = signal(false);

  // Límites de contenido (peso/imágenes/texto) de la descripción para mitigar el peso en BD.
  // Getter/método (acceso lazy) para no leer los imports en tiempo de
  // construcción: evita un undefined según el orden de carga de módulos al
  // bundlear los specs juntos.
  protected get limits() {
    return DEFAULT_MESSAGE_LIMITS;
  }
  protected formatBytes(bytes: number): string {
    return formatBytes(bytes);
  }
  protected readonly stats = computed(() => analyzeMessageContent(this.description()));
  protected readonly limitErrors = computed(() => getMessageLimitErrors(this.stats(), this.limits));

  protected readonly isValid = computed(
    () =>
      this.title().trim().length > 0 &&
      this.plainText(this.description()).trim().length > 0 &&
      this.limitErrors().length === 0
  );

  protected readonly typeOptions = [
    { label: 'Soporte', value: TicketType.Support, icon: 'pi pi-headphones' },
    { label: 'Error', value: TicketType.Bug, icon: 'pi pi-bug' },
    { label: 'Mejora', value: TicketType.Improvement, icon: 'pi pi-arrow-up-right' },
    { label: 'Sugerencia', value: TicketType.Suggestion, icon: 'pi pi-lightbulb' },
  ];

  protected readonly priorityOptions = [
    { label: 'Baja', value: TicketPriority.Low },
    { label: 'Media', value: TicketPriority.Medium },
    { label: 'Alta', value: TicketPriority.High },
    { label: 'Crítica', value: TicketPriority.Critical },
  ];

  onShow(): void {
    this.title.set('');
    this.description.set('');
    this.type.set(TicketType.Support);
    this.priority.set(TicketPriority.Medium);
    this.selectedTagIds.set([]);
    this.isSaving.set(false);
  }

  /** Extrae texto plano del HTML del editor para validar que no esté vacío */
  private plainText(html: string): string {
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
  }

  /**
   * Engancha el handler de imagen optimizada de Quill al iniciar el editor.
   * Las imágenes de la descripción quedan embebidas (base64) ya optimizadas.
   */
  onEditorInit(event: { editor: unknown }): void {
    registerOptimizedImageHandler(event.editor as never, {
      onTooLarge: () =>
        this.toast.warn('La imagen es demasiado pesada incluso optimizada. Usa una imagen más pequeña.'),
      onError: () => this.toast.error('No se pudo procesar la imagen.'),
    });
  }

  save(): void {
    if (!this.isValid()) return;

    const tenantId = this.authService.user()?.tenantId;
    if (!tenantId) return;

    const title = this.title().trim();
    const description = this.description();
    const type = this.type();
    const priority = this.priority();
    const tagIds = this.selectedTagIds().length ? this.selectedTagIds() : null;

    this.isSaving.set(true);
    this.ticketsService
      .create({ tenantId, title, description, type, priority, tagIds })
      // DESHABILITADO TEMPORALMENTE: la subida de imágenes embebidas como adjuntos al
      // backend está dando problemas. Por ahora la imagen queda solo en el HTML del editor
      // (base64) y NO se procesa como adjunto. No eliminar: reactivar cuando se resuelva.
      // .pipe(
      //   switchMap((res) => {
      //     const ticketId = res.data?.id;
      //     if (!ticketId) return of(null);
      //     // Sube las imágenes embebidas (base64) como adjuntos. Si la descripción cambia,
      //     // se reescribe el ticket con las URLs definitivas (el id ya existe tras crear).
      //     return this.ticketsService
      //       .processInlineImages(ticketId, description)
      //       .pipe(
      //         switchMap((finalHtml) =>
      //           finalHtml === description
      //             ? of(null)
      //             : this.ticketsService.update(ticketId, { title, description: finalHtml, type, priority, tagIds })
      //         )
      //       );
      //   })
      // )
      .subscribe({
        next: () => {
          this.toast.success('Ticket creado correctamente');
          this.saved.emit();
          this.close();
        },
        error: () => {
          this.toast.error('Error al crear el ticket');
          this.isSaving.set(false);
        },
      });
  }

  close(): void {
    this.isSaving.set(false);
    this.visibleChange.emit(false);
  }
}
